import { useEffect, useRef, useState } from 'react';
import { API_BASE } from '../../lib/apiBase.js';
import { Button } from '../ds/index.js';
import { draftBody, type AvatarDraft } from './pcAppearance.js';

const FRAME = 48;
/** Ordre de rotation (rangées de la planche : bas 0, gauche 1, droite 2, haut 3). */
const TURN = [0, 1, 3, 2] as const;
const DIRECTION_LABELS = ['de face', 'de profil gauche', 'de dos', 'de profil droit'];
/** Personnage d'environ 24 px de large, des pieds (y 46) au haut de la tête (y 4). */
const BOX = { x: 12, y: 4, w: 24, h: 42 };

/**
 * Grand aperçu de l'avatar en cours de création : planche assemblée par l'API
 * (POST /avatars/preview, rien n'est stocké), demandée 250 ms après le dernier
 * changement. Une réponse arrivée après un changement plus récent est ignorée.
 */
export function PcPreview({ draft, headers, height }: {
  /** Pixel Citizen (v2) ou monde du campus (v3) : même format de planche. */
  draft: AvatarDraft;
  headers: Record<string, string>;
  /** Hauteur du personnage à l'écran (px) — multiple de 42 pour des pixels nets. */
  height: number;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [turn, setTurn] = useState(0);
  const generation = useRef(0);
  const headersRef = useRef(headers);
  headersRef.current = headers;
  const key = JSON.stringify(draftBody(draft));

  useEffect(() => {
    const gen = ++generation.current;
    setLoading(true);
    const timer = setTimeout(() => {
      fetch(`${API_BASE}/avatars/preview`, {
        method: 'POST',
        headers: { ...headersRef.current, 'Content-Type': 'application/json' },
        body: key,
      })
        .then(async (res) => {
          if (!res.ok) throw new Error(String(res.status));
          const blob = await res.blob();
          if (gen !== generation.current) return;
          setUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(blob); });
          setFailed(false);
          setLoading(false);
        })
        .catch(() => {
          if (gen !== generation.current) return;
          setFailed(true);
          setLoading(false);
        });
    }, 250);
    return () => clearTimeout(timer);
  }, [key]);

  useEffect(() => () => { setUrl((old) => { if (old) URL.revokeObjectURL(old); return null; }); }, []);

  const scale = height / BOX.h;
  const row = TURN[turn]!;

  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div
        role="img"
        aria-label={`Aperçu de ton avatar, ${DIRECTION_LABELS[turn]}`}
        style={{
          width: BOX.w * scale, height, opacity: loading && url ? 0.55 : 1, transition: 'opacity 120ms',
          backgroundImage: url ? `url(${url})` : undefined,
          backgroundRepeat: 'no-repeat',
          backgroundSize: `auto ${4 * FRAME * scale}px`, // 16 ou 20 colonnes selon le style
          backgroundPosition: `${-BOX.x * scale}px ${-(row * FRAME + BOX.y) * scale}px`,
          imageRendering: 'pixelated',
        }}
      />
      {failed && !url && (
        <span style={{ position: 'absolute', top: '40%', fontSize: 'var(--text-label)', color: 'var(--color-text-secondary)', textAlign: 'center' }}>
          Aperçu indisponible
        </span>
      )}
      <div style={{ display: 'flex', gap: 4 }}>
        <Button variant="ghost" size="sm" icon="chevron-left" aria-label="Tourner vers la gauche" onClick={() => setTurn((t) => (t + 3) % 4)} />
        <Button variant="ghost" size="sm" icon="chevron-right" aria-label="Tourner vers la droite" onClick={() => setTurn((t) => (t + 1) % 4)} />
      </div>
    </div>
  );
}
